import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { Redirect } from "@redotech/react-router-util/redirect";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { reviewsRoutes } from "@redotech/redo-merchant-review-app/route";
import { memo } from "react";
import { matchPath, Navigate, Outlet } from "react-router-dom";
import {
  MARKETING_ANALYTICS_TABS_URL_PARAM,
  MarketingAnalyticsPage,
} from "../../../analytics/marketing/marketing-analytics";
import {
  automationRoutes,
  backwardsCompatibleEmailSmsRoutes,
  emailSmsRoutes,
  loyaltyRoutes,
} from "../../../marketing/route";

const HIDE_PADDING_ROUTES: string[] = [
  "/reviews/all-reviews",
  "/reviews/products",
  "/email-sms/audience",
  "/email-sms/audience/customers/:customerId",
  "/automations/*",
  "/email-sms/campaigns/*",
  "/email-sms/segments/*",
  "/email-sms/templates/email/*",
  "/email-sms/templates/sms/*",
  "/email-sms/templates/click-map/*",
  "/email-sms/campaigns/:campaignId/details",
  "/email-sms/signups/*",
  "/email-sms/ai-personas/*",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/members",
  "/loyalty/page/customize",
  "/tracking/tracking-page/login-form-builder",
  "/tracking/tracking-page/tracking-page-builder",
  "/tracking/tracking-page/edit-flow",
  "/tracking/orders",
  "/tracking/shipments",
  "/tracking/shipments/:viewName",
  "/email-sms/deliverability/*",
  "/recover/conversation-log",
  "/recover/builder/:recoverConfigId",
  "/marketing-agent/conversations",
  "/marketing-agent",
  "/text-concierge/setup",
  "/text-concierge/conversations",
  "/text-concierge/conversations/:conversationId",
  "/text-concierge/settings",
  "/contextual",
  "/contextual/ai",
  "/contextual/ai/v3",
];

function hidePadding(url: string, search: { search: string }): boolean {
  if (
    url.includes("/marketing/analytics") &&
    new URLSearchParams(search.search).get(
      MARKETING_ANALYTICS_TABS_URL_PARAM,
    ) === MarketingAnalyticsPage.BACK_IN_STOCK
  ) {
    return true;
  }

  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/upsell-page/:id",
  "/email-sms/signups/builder/:id",
  "/email-sms/templates/email/:id",
  "/email-sms/templates/sms/:id",
  "/email-sms/templates/click-map/:templateId",
  "/email-sms/segments/:segmentId",
  "/email-sms/campaigns/:campaignId/edit/*",
  "/email-sms/campaigns/:campaignId/edit-with-ai/template",
  "/automations/:automationId",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/page/customize",
  "/tracking/tracking-page/login-form-builder",
  "/tracking/tracking-page/tracking-page-builder",
  "/tracking/tracking-page/edit-flow",
  "/recover/builder/:recoverConfigId",
];

const HIDE_HEADER_ROUTES: string[] = [
  "/tracking/about",
  "/contextual/ai",
  "/contextual/ai/v3",
  ...FULLSCREEN_ROUTES,
];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [
  "/contextual/ai",
  "/contextual/ai/v3",
  ...FULLSCREEN_ROUTES,
];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

function hideSearchAndBanner(url: string): boolean {
  return FULLSCREEN_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const ContextualFeatureGate = memo(function ContextualFeatureGate() {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const flagLoad = useLoad(
    (signal) =>
      marketingRpcClient.getBooleanFeatureFlag(
        { key: FeatureFlagKey.CONTEXTUAL_ONBOARDING },
        { signal },
      ),
    [marketingRpcClient],
  );

  if (flagLoad.pending || flagLoad.value === undefined) {
    return null;
  }

  if (!flagLoad.value.enabled) {
    return <Navigate replace to=".." />;
  }

  return <Outlet />;
});

// cspell:ignore Permissioned
export const marketingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Marketing" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
            hideSearchBarAndBanner={hideSearchAndBanner}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="email-sms" /> },
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { MarketingWelcomePage } = await import(
            "@redotech/redo-merchant-marketing-app/welcome/marketing-welcome-page"
          );
          return { Component: MarketingWelcomePage };
        },
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingAnalytics } = await import(
                "@redotech/redo-merchant-app/analytics/marketing/marketing-analytics"
              );
              return { Component: MarketingAnalytics };
            },
          },
          {
            path: "order-tracking",
            handle: { breadcrumb: "Order Tracking" },
            async lazy() {
              const { OrderTrackingAnalytics } = await import(
                "@redotech/redo-merchant-app/analytics/order-tracking/order-tracking-analytics"
              );
              return { Component: OrderTrackingAnalytics };
            },
          },
        ],
      },
      {
        path: "marketing-agent",
        handle: { breadcrumb: "Marketing Agent" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingAgentIndexRoute } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-index-route"
              );
              return { Component: MarketingAgentIndexRoute };
            },
          },
          {
            path: "dashboard",
            handle: { breadcrumb: "Dashboard" },
            async lazy() {
              const { MarketingAgentDashboardPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-split/pages/dashboard/page"
              );
              return { Component: MarketingAgentDashboardPage };
            },
          },
          {
            path: "analytics",
            handle: { breadcrumb: "Analytics" },
            async lazy() {
              const { MarketingAgentAnalyticsPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-split/pages/analytics/page"
              );
              return { Component: MarketingAgentAnalyticsPage };
            },
          },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            async lazy() {
              const { MarketingAgentConversationsPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-split/pages/conversations/page"
              );
              return { Component: MarketingAgentConversationsPage };
            },
          },
        ],
      },
      { path: "reviews", children: reviewsRoutes },
      { path: "loyalty", children: loyaltyRoutes },
      {
        path: "recover",
        children: [
          {
            index: true,
            lazy: async () => {
              const { RecoverLandingPage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-landing-page"
              );
              return { Component: RecoverLandingPage };
            },
          },
          {
            path: "welcome",
            handle: { breadcrumb: "Welcome" },
            async lazy() {
              const { RecoverWelcomePage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-welcome-page"
              );
              return { Component: RecoverWelcomePage };
            },
          },
          {
            path: "conversation-log",
            handle: { breadcrumb: "Conversation log" },
            async lazy() {
              const { RecoverConversationLogPage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-conversation-log-page"
              );
              return { Component: RecoverConversationLogPage };
            },
          },
          {
            path: "builder/:recoverConfigId",
            async lazy() {
              const { RecoverBuilder } = await import(
                "@redotech/redo-merchant-marketing-app/recover-builder/recover-builder"
              );
              return { Component: RecoverBuilder };
            },
          },
        ],
      },
      {
        path: "tracking",
        children: [
          {
            index: true,
            lazy: async () => {
              const { OrderTrackingLandingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/order-tracking-landing-page"
              );
              return { Component: OrderTrackingLandingPage };
            },
          },
          {
            path: "about",
            handle: { breadcrumb: "Order tracking", breadcrumbNavigate: false },
            async lazy() {
              const { AboutTracking } = await import(
                "@redotech/redo-merchant-app/setting/about-tracking/about-tracking"
              );
              return { element: <AboutTracking /> };
            },
          },
          {
            path: "orders",
            handle: { breadcrumb: "Orders" },
            lazy: async () => {
              const { Orders } = await import(
                "@redotech/redo-merchant-app/order/orders-table/orders-table"
              );
              return {
                element: (
                  <Orders
                    filterParams={(filters) => ({
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      dateRange: filters.dateRange,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      shipmentStatus: filters.shipmentStatus,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      carrier: filters.carrier,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      destinationState: filters.destinationState,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      destinationCountry: filters.destinationCountry,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      originState: filters.originState,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      originCountry: filters.originCountry,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      dateFilter: filters.dateFilter,
                    })}
                  />
                ),
              };
            },
          },
          {
            path: "shipments",
            handle: { breadcrumb: "Shipments" },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } = await import(
                    "@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page"
                  );
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
              {
                path: ":viewName",
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } = await import(
                    "@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page"
                  );
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
            ],
          },
          {
            path: "tracking-page/login-form-builder",
            lazy: async () => {
              const { OrderLookupSettingsBuilderPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/order-lookup-settings-builder"
              );
              return { Component: OrderLookupSettingsBuilderPage };
            },
          },
          {
            path: "tracking-page/tracking-page-builder",
            lazy: async () => {
              const { TrackingPageBuilderPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page-builder"
              );
              return { Component: TrackingPageBuilderPage };
            },
          },
          {
            path: "tracking-page/edit-flow",
            lazy: async () => {
              const { EditTrackingPageFlowPage } = await import(
                "../../../setting/tracking-page-flow"
              );
              return { Component: EditTrackingPageFlowPage };
            },
          },
          {
            path: "tracking-page",
            handle: { breadcrumb: "Tracking page" },
            lazy: async () => {
              const { TrackingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page"
              );
              return { Component: TrackingPage };
            },
          },
        ],
      },
      {
        path: "contextual",
        handle: { breadcrumb: "Contextual" },
        element: <ContextualFeatureGate />,
        children: [
          {
            index: true,
            lazy: async () => {
              const { AiContextualPage } = await import(
                "@redotech/redo-merchant-marketing-app/contextual-ai/ai-contextual-page"
              );
              return { Component: AiContextualPage };
            },
          },
          {
            path: "dashboard",
            handle: { breadcrumb: "Dashboard" },
            lazy: async () => {
              const { ContextualLandingPage } = await import(
                "@redotech/redo-merchant-marketing-app/contextual/contextual-landing-page"
              );
              return { Component: ContextualLandingPage };
            },
          },
        ],
      },
      {
        path: "text-concierge",
        handle: { breadcrumb: "Text Concierge" },
        children: [
          {
            index: true,
            async lazy() {
              const { TextConciergeIndexPage } = await import(
                "@redotech/redo-merchant-marketing-app/text-concierge/text-concierge-index-page"
              );
              return { Component: TextConciergeIndexPage };
            },
          },
          {
            path: "setup",
            handle: { breadcrumb: "Setup" },
            async lazy() {
              const { TextConciergeSetupPage } = await import(
                "@redotech/redo-merchant-marketing-app/text-concierge/setup/text-concierge-setup-page"
              );
              return { Component: TextConciergeSetupPage };
            },
          },
          {
            path: "settings",
            handle: { breadcrumb: "Settings" },
            async lazy() {
              const { TextConciergeSettingsPage } = await import(
                "@redotech/redo-merchant-marketing-app/text-concierge/settings/text-concierge-settings-page"
              );
              return { Component: TextConciergeSettingsPage };
            },
          },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            children: [
              {
                index: true,
                async lazy() {
                  const { TextConciergeConversationsListPage } = await import(
                    "@redotech/redo-merchant-marketing-app/text-concierge/conversations/conversations-list-page"
                  );
                  return { Component: TextConciergeConversationsListPage };
                },
              },
              {
                path: ":conversationId",
                handle: { breadcrumb: "Detail" },
                async lazy() {
                  const { ConversationDetailPage } = await import(
                    "@redotech/redo-merchant-marketing-app/text-concierge/conversations/conversation-detail-page"
                  );
                  return { Component: ConversationDetailPage };
                },
              },
            ],
          },
        ],
      },
      { path: "email-sms", children: emailSmsRoutes },
      { path: "automations", children: automationRoutes },
      {
        path: "email-builder/click-map/:templateId",
        handle: { breadcrumb: "Click Map" },
        async lazy() {
          const { FullscreenClickMap } = await import(
            "@redotech/redo-merchant-marketing-app/email-builder/click-map/fullscreen-click-map"
          );
          return { Component: FullscreenClickMap };
        },
      },
      ...backwardsCompatibleEmailSmsRoutes,
    ],
  },
];

export const backwardsCompatibleMarketingRoutes: PermissionedRouteObject[] = [
  {
    path: "reviews",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "tracking",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
        ],
      },
    ],
  },
];
